import { fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Tag, message, Popconfirm, Space, Typography, Empty, Modal, Form, Input, Segmented,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getApplications, approveApplication, rejectApplication, type PartnerApplication,
} from '../../services/partnerApplicationService';

const { Title, Paragraph, Text } = Typography;

const shortId = (id?: string | null) => (id ? id.replace(/-/g, '').slice(0, 8) : '—');

const STATUS_COLOR: Record<string, string> = {
  submitted: 'gold',
  approved: 'green',
  rejected: 'red',
};

const PartnerApplicationsPage: React.FC = () => {
  const [rows, setRows] = useState<PartnerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('submitted');
  const [acting, setActing] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<PartnerApplication | null>(null);
  const [form] = Form.useForm();

  const load = async (s = status) => {
    setLoading(true);
    try {
      setRows(await getApplications(s));
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load partner applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const onApprove = async (id: string) => {
    setActing(id);
    try {
      await approveApplication(id);
      message.success('Partner approved — the owner has been invited by email.');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Approval failed. Please try again.');
    } finally {
      setActing(null);
    }
  };

  const onReject = async () => {
    if (!rejectFor) return;
    const { reason } = await form.validateFields().catch(() => ({ reason: undefined }));
    setActing(rejectFor.id);
    try {
      await rejectApplication(rejectFor.id, reason);
      message.success('Application rejected.');
      setRejectFor(null);
      form.resetFields();
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not reject the application.');
    } finally {
      setActing(null);
    }
  };

  const columns = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (v: string, r: PartnerApplication) => (
        <div>
          <Text strong>{v}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.country || '—'}{r.taxId ? ` · ${r.taxId}` : ''}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: unknown, r: PartnerApplication) => (
        <div>
          <div>{r.contactName}</div>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.email}{r.phone ? ` · ${r.phone}` : ''}</Text></div>
        </div>
      ),
    },
    {
      title: 'Track',
      dataIndex: 'requestedPartnerType',
      key: 'requestedPartnerType',
      render: (v?: string | null) => (
        <Tag color={v === 'referral' ? 'green' : v === 'solution' ? 'blue' : v === 'implementation' ? 'purple' : 'gold'}>
          {v || 'financial'}
        </Tag>
      ),
    },
    {
      title: 'Intent / background',
      key: 'fundingIntent',
      render: (_: unknown, r: PartnerApplication & { notes?: string; industry?: string }) => {
        const v = r.fundingIntent || r.notes;
        return (
          <div>
            {r.industry ? <Text type="secondary" style={{ fontSize: 12 }}>{r.industry}</Text> : null}
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {v ? (v.length > 120 ? `${v.slice(0, 120)}…` : v) : '—'}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{fmtDateTime(v)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: string, r: PartnerApplication) => (
        <div>
          <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
          {v === 'approved' && r.tenantId && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>partner {shortId(r.partnerId)}</Text></div>
          )}
          {v === 'rejected' && r.rejectionReason && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>{r.rejectionReason}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, r: PartnerApplication) =>
        r.status === 'submitted' ? (
          <Space>
            <Popconfirm
              title="Approve this partner?"
              description="This creates the partner + workspace and emails the contact an owner invite."
              okText="Approve"
              onConfirm={() => onApprove(r.id)}
            >
              <Button type="primary" size="small" icon={<CheckCircleOutlined />} loading={acting === r.id}>
                Approve
              </Button>
            </Popconfirm>
            <Button
              danger size="small" icon={<CloseCircleOutlined />}
              onClick={() => { setRejectFor(r); form.resetFields(); }}
            >
              Reject
            </Button>
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Reviewed {fmtDateTime(r.reviewedAt)}</Text>
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>Partner Applications</Title>
          <Paragraph type="secondary" style={{ maxWidth: 640 }}>
            Prospective lending partners who applied via “Become a partner”. Approving one provisions
            the partner and its workspace, then emails the contact an invitation to set up as the
            first owner.
          </Paragraph>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => load()}>Refresh</Button>
      </div>

      <Segmented
        style={{ margin: '8px 0 16px' }}
        value={status}
        onChange={(v) => setStatus(String(v))}
        options={[
          { label: 'Pending', value: 'submitted' },
          { label: 'Approved', value: 'approved' },
          { label: 'Rejected', value: 'rejected' },
        ]}
      />

      <Table
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={rows}
        locale={{ emptyText: <Empty description="No applications in this view" /> }}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />

      <Modal
        open={!!rejectFor}
        title={`Reject ${rejectFor?.companyName || 'application'}`}
        okText="Reject"
        okButtonProps={{ danger: true, loading: acting === rejectFor?.id }}
        onOk={onReject}
        onCancel={() => { setRejectFor(null); form.resetFields(); }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason (optional — shared internally)"
          >
            <Input.TextArea rows={3} placeholder="e.g. incomplete details, out of coverage area" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PartnerApplicationsPage;
